import React, { useMemo, useState } from "react";
import Papa from "papaparse";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";

const IGNORE = "__IGNORE__";

const LEAD_CREATE_FIELDS = [
  "full_name",
  "phone_number",
  "email",
  "state",
  "county",
  "dob",
  "lead_source",
  "campaign_name",
  "ad_set_name",
  "ad_name",
  "form_name",
  "lead_external_id",
  "lead_type",
  "referral_agent_name",
  "lead_create_date",
];

function normalizeKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function toIsoDateMaybe(v) {
  if (!v) return undefined;
  const s = String(v).trim();
  if (!s) return undefined;

  const m1 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m1) return s;

  const m2 = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m2) {
    const mm = String(m2[1]).padStart(2, "0");
    const dd = String(m2[2]).padStart(2, "0");
    const yyyy = m2[3];
    return `${yyyy}-${mm}-${dd}`;
  }

  return s;
}

function coerceValue(field, raw) {
  if (raw === null || raw === undefined) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;

  if (field === "dob") return toIsoDateMaybe(s);
  if (field === "lead_create_date") return toIsoDateMaybe(s);
  return s;
}

export default function ImportCsvWizard({ onCancel, onBack, onImported }) {
  const { toast } = useToast();

  const [csvColumns, setCsvColumns] = useState([]);
  const [csvRows, setCsvRows] = useState([]);
  const [mapping, setMapping] = useState({});

  const [step, setStep] = useState(1); // 1 upload, 2 map, 3 import, 4 done
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, created: 0, skipped: 0 });

  const previewRows = useMemo(() => csvRows.slice(0, 20), [csvRows]);

  const reset = () => {
    setCsvColumns([]);
    setCsvRows([]);
    setMapping({});
    setStep(1);
    setImporting(false);
    setProgress({ done: 0, total: 0, created: 0, skipped: 0 });
  };

  const autoMap = (cols) => {
    const normCols = new Map(cols.map((c) => [normalizeKey(c), c]));
    const next = {};
    LEAD_CREATE_FIELDS.forEach((f) => {
      const want = normalizeKey(f);
      next[f] = normCols.get(want) || IGNORE;
    });

    // Common aliases
    if (!next.full_name || next.full_name === IGNORE) next.full_name = normCols.get("fullname") || normCols.get("name") || IGNORE;
    if (!next.phone_number || next.phone_number === IGNORE) next.phone_number = normCols.get("phone") || normCols.get("phonenumber") || IGNORE;
    if (!next.lead_create_date || next.lead_create_date === IGNORE)
      next.lead_create_date = normCols.get("leadcreatedate") || normCols.get("createdate") || IGNORE;

    return next;
  };

  const onPickFile = (f) => {
    if (!f) return;

    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const cols = results?.meta?.fields || [];
        const rows = results?.data || [];

        if (!cols.length) {
          toast({
            title: "CSV needs a header row",
            description: "Please upload a CSV where the first row contains column names.",
            variant: "destructive",
          });
          return;
        }

        setCsvColumns(cols);
        setCsvRows(rows);
        setMapping(autoMap(cols));
        setStep(2);
      },
      error: (err) => {
        toast({ title: "Could not parse CSV", description: String(err), variant: "destructive" });
      },
    });
  };

  const canImport = useMemo(() => {
    return Boolean(mapping.full_name) && mapping.full_name !== IGNORE;
  }, [mapping.full_name]);

  const buildLeadObjects = () => {
    const leads = [];
    let skipped = 0;

    csvRows.forEach((row) => {
      const obj = {};
      LEAD_CREATE_FIELDS.forEach((field) => {
        const col = mapping[field];
        if (!col || col === IGNORE) return;
        const v = coerceValue(field, row[col]);
        if (v !== undefined) obj[field] = v;
      });

      if (!obj.full_name) {
        skipped += 1;
        return;
      }

      obj.full_name = String(obj.full_name).trim();
      leads.push(obj);
    });

    return { leads, skipped };
  };

  const runImport = async () => {
    if (!canImport) {
      toast({ title: "Full Name is required", description: "Map a CSV column to full_name.", variant: "destructive" });
      return;
    }

    const { leads, skipped } = buildLeadObjects();
    if (leads.length === 0) {
      toast({ title: "No valid rows", description: "No rows had a Full Name value.", variant: "destructive" });
      return;
    }

    setImporting(true);
    setStep(3);

    const chunkSize = 200;
    let done = 0;
    let created = 0;

    setProgress({ done: 0, total: leads.length, created: 0, skipped });

    try {
      for (let i = 0; i < leads.length; i += chunkSize) {
        const chunk = leads.slice(i, i + chunkSize);
        const res = await api.post("/leads/bulk-create", { items: chunk });
        created += Number(res.data.created || 0);
        done += chunk.length;
        setProgress({ done, total: leads.length, created, skipped });
      }

      toast({ title: "Import complete", description: `Created ${created} leads${skipped ? ` (skipped ${skipped})` : ""}.` });
      setStep(4);
      onImported?.({ created, skipped });
    } catch (e) {
      toast({ title: "Import failed", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
      setStep(2);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="max-h-[70vh] overflow-hidden flex flex-col">
      <div className="flex-1 overflow-auto pr-1">
        {step === 1 ? (
          <div className="space-y-2">
            <Label className="text-slate-200/80">CSV File</Label>
            <Input type="file" accept=".csv,text/csv" className="bg-white/5 ring-1 ring-white/10" onChange={(e) => onPickFile(e.target.files?.[0] || null)} />
            <div className="text-xs text-slate-200/60">Tip: the first row should contain column names (e.g., full_name, phone_number, email, lead_create_date).</div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <div className="text-sm font-medium text-slate-100">Column mapping</div>
                <div className="text-xs text-slate-200/60">Full Name is required.</div>
              </div>
              <div className="flex items-start justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setMapping(autoMap(csvColumns))}
                >
                  Auto-map
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 rounded-2xl bg-black/20 p-3 ring-1 ring-white/10 sm:grid-cols-2">
              {LEAD_CREATE_FIELDS.map((field) => (
                <div key={field} className="space-y-1">
                  <div className={"text-xs " + (field === "full_name" ? "text-indigo-200" : "text-slate-200/70")}>
                    {field} {field === "full_name" ? "*" : ""}
                  </div>
                  <Select value={mapping[field] || IGNORE} onValueChange={(v) => setMapping((m) => ({ ...m, [field]: v }))}>
                    <SelectTrigger className="bg-white/5 ring-1 ring-white/10">
                      <SelectValue placeholder="Ignore" />
                    </SelectTrigger>
                    <SelectContent className="glass border-white/10">
                      <SelectItem value={IGNORE}>Ignore</SelectItem>
                      {csvColumns.map((c) => (
                        <SelectItem key={c} value={c}>
                          {c}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-slate-100">Preview (first {previewRows.length} rows)</div>
              <div className="overflow-auto rounded-2xl ring-1 ring-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-white/5 text-slate-200/70">
                    <tr>
                      {csvColumns.slice(0, 8).map((c) => (
                        <th key={c} className="px-3 py-2 text-left">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, idx) => (
                      <tr key={idx} className="border-t border-white/10">
                        {csvColumns.slice(0, 8).map((c) => (
                          <td key={c} className="px-3 py-2 text-slate-100/90">
                            {String(r[c] ?? "")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-100">Importing…</div>
            <div className="text-sm text-slate-200/70">
              Imported {progress.done} / {progress.total} • Created: {progress.created}
              {progress.skipped ? ` • Skipped: ${progress.skipped}` : ""}
            </div>
          </div>
        ) : null}

        {step === 4 ? (
          <div className="space-y-2">
            <div className="text-sm font-medium text-slate-100">Done</div>
            <div className="text-sm text-slate-200/70">
              Created {progress.created} leads{progress.skipped ? ` (skipped ${progress.skipped})` : ""}.
            </div>
          </div>
        ) : null}
      </div>

      <div className="pt-4 flex items-center justify-between gap-2">
        <div />

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              reset();
              onCancel?.();
            }}
            disabled={importing}
          >
            {step === 4 ? "Close" : "Cancel"}
          </Button>

          {step === 2 ? (
            <Button
              type="button"
              onClick={runImport}
              disabled={importing || !canImport}
            >
              Import
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
